import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { 
  LocationResult, 
  SavedLocation, 
  LocationInput 
} from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface LocationStore {
  locations: SavedLocation[]
  maxLocations: number
  
  addLocation: (location: Omit<SavedLocation, 'id' | 'createdAt'>) => void
  addBatchLocations: (locations: Omit<SavedLocation, 'id' | 'createdAt'>[]) => void
  saveLocation: (result: LocationResult, input?: LocationInput) => Promise<void>
  updateLocation: (id: string, updates: Partial<SavedLocation>) => void
  deleteLocation: (id: string) => void
  clearLocations: () => void
  
  getLocationById: (id: string) => SavedLocation | undefined
  getLocationsByWorld: (worldId: string) => SavedLocation[]
  getLocationsByDanger: (minLevel: number, maxLevel: number) => SavedLocation[]
  searchLocations: (query: string) => SavedLocation[]
  
  markAsVisited: (id: string) => void
  updateRatings: (id: string, dangerRating?: number, beautyRating?: number) => void
  toggleFavorite: (id: string) => void
  updateMemo: (id: string, memo: string) => void
  addTag: (id: string, tag: string) => void
  removeTag: (id: string, tag: string) => void
  
  exportLocations: () => string
  importLocations: (jsonString: string) => void
  
  getStatistics: () => {
    total: number
    visited: number
    byType: Record<string, number>
    byDanger: Record<string, number>
    byWorld: Record<string, number>
    favorites: number
  }
}

const MAX_LOCATIONS = 100

function generateLocationId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

export const useLocationStore = create<LocationStore>()(
  persist(
    (set, get) => ({
      locations: [],
      maxLocations: MAX_LOCATIONS,
      
      addLocation: (location) => {
        const newLocation: SavedLocation = {
          ...location,
          id: generateLocationId(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          visited: location.visited ?? false,
          dangerRating: location.dangerRating ?? location.result.basicInfo.accessibility?.difficulty ?? 5,
          beautyRating: location.beautyRating ?? 5,
          favorite: location.favorite ?? false
        }
        
        set(state => {
          const locations = [newLocation, ...state.locations]
          if (locations.length > MAX_LOCATIONS) {
            locations.pop()
          }
          return { locations }
        })
      },
      
      addBatchLocations: (batchLocations) => {
        const timestamp = Date.now()
        const newLocations = batchLocations.map((location, index) => ({
          ...location,
          id: `${timestamp + index}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          visited: location.visited ?? false,
          dangerRating: location.dangerRating ?? location.result.basicInfo.accessibility?.difficulty ?? 5,
          beautyRating: location.beautyRating ?? 5,
          favorite: location.favorite ?? false
        }))
        
        set(state => {
          let locations = [...newLocations, ...state.locations]
          if (locations.length > MAX_LOCATIONS) {
            locations = locations.slice(0, MAX_LOCATIONS)
          }
          return { locations }
        })
      },
      
      saveLocation: async (result, input) => {
        const location: Omit<SavedLocation, 'id' | 'createdAt'> = {
          title: result.basicInfo.name,
          input: input || {},
          result,
          updatedAt: new Date().toISOString()
        }
        get().addLocation(location)
      },
      
      updateLocation: (id, updates) => {
        set(state => ({
          locations: state.locations.map(loc =>
            loc.id === id 
              ? { ...loc, ...updates, updatedAt: new Date().toISOString() }
              : loc
          )
        }))
      },
      
      deleteLocation: (id) => {
        set(state => ({
          locations: state.locations.filter(loc => loc.id !== id)
        }))
      },
      
      clearLocations: () => {
        set({ locations: [] })
      },
      
      getLocationById: (id) => {
        return get().locations.find(loc => loc.id === id)
      },
      
      getLocationsByWorld: (worldId) => {
        return get().locations.filter(loc => loc.worldId === worldId)
      },
      
      getLocationsByDanger: (minLevel, maxLevel) => {
        return get().locations.filter(loc => {
          const danger = loc.dangerRating || 5
          return danger >= minLevel && danger <= maxLevel
        })
      },
      
      searchLocations: (query) => {
        const lowerQuery = query.toLowerCase()
        return get().locations.filter(loc => {
          const result = loc.result
          return (
            result.basicInfo.name.toLowerCase().includes(lowerQuery) ||
            result.basicInfo.type.toLowerCase().includes(lowerQuery) ||
            result.basicInfo.description.toLowerCase().includes(lowerQuery) ||
            result.basicInfo.summary.toLowerCase().includes(lowerQuery) ||
            result.basicInfo.aliases?.some(alias => 
              alias.toLowerCase().includes(lowerQuery)
            ) ||
            loc.tags?.some(tag => 
              tag.toLowerCase().includes(lowerQuery)
            )
          )
        })
      },
      
      markAsVisited: (id) => {
        set(state => ({
          locations: state.locations.map(loc =>
            loc.id === id 
              ? { ...loc, visited: true, updatedAt: new Date().toISOString() }
              : loc
          )
        }))
      },
      
      updateRatings: (id, dangerRating, beautyRating) => {
        set(state => ({
          locations: state.locations.map(loc =>
            loc.id === id 
              ? { 
                  ...loc, 
                  dangerRating: dangerRating ?? loc.dangerRating,
                  beautyRating: beautyRating ?? loc.beautyRating,
                  updatedAt: new Date().toISOString()
                } 
              : loc
          )
        }))
      },
      
      toggleFavorite: (id) => {
        set(state => ({
          locations: state.locations.map(loc =>
            loc.id === id 
              ? { ...loc, favorite: !loc.favorite, updatedAt: new Date().toISOString() }
              : loc
          )
        }))
      },
      
      updateMemo: (id, memo) => {
        set(state => ({
          locations: state.locations.map(loc =>
            loc.id === id 
              ? { ...loc, memo, updatedAt: new Date().toISOString() }
              : loc
          )
        }))
      },
      
      addTag: (id, tag) => {
        set(state => ({
          locations: state.locations.map(loc =>
            loc.id === id 
              ? { 
                  ...loc, 
                  tags: [...(loc.tags || []), tag],
                  updatedAt: new Date().toISOString()
                }
              : loc
          )
        }))
      },
      
      removeTag: (id, tag) => {
        set(state => ({
          locations: state.locations.map(loc =>
            loc.id === id 
              ? { 
                  ...loc, 
                  tags: (loc.tags || []).filter(t => t !== tag),
                  updatedAt: new Date().toISOString()
                }
              : loc
          )
        }))
      },
      
      exportLocations: () => {
        const { locations } = get()
        return JSON.stringify(locations, null, 2)
      },
      
      importLocations: (jsonString) => {
        try {
          const imported = JSON.parse(jsonString)
          const locations = Array.isArray(imported) ? imported : imported.locations || []
          
          const validLocations = locations
            .filter(loc => loc.result && loc.result.basicInfo)
            .map(loc => ({
              ...loc,
              id: loc.id || generateLocationId(),
              createdAt: loc.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }))
          
          set({ locations: validLocations })
        } catch (error) {
          throw new Error('インポートデータの形式が不正です')
        }
      },
      
      getStatistics: () => {
        const { locations } = get()
        const stats = {
          total: locations.length,
          visited: locations.filter(l => l.visited).length,
          byType: {} as Record<string, number>,
          byDanger: {} as Record<string, number>,
          byWorld: {} as Record<string, number>,
          favorites: locations.filter(l => l.favorite).length
        }
        
        locations.forEach(loc => {
          const type = loc.result.basicInfo.type
          stats.byType[type] = (stats.byType[type] || 0) + 1
          
          const danger = Math.floor((loc.dangerRating || 5) / 2)
          const dangerKey = `danger_${danger * 2}-${danger * 2 + 2}`
          stats.byDanger[dangerKey] = (stats.byDanger[dangerKey] || 0) + 1
          
          if (loc.worldId) {
            stats.byWorld[loc.worldId] = (stats.byWorld[loc.worldId] || 0) + 1
          }
        })
        
        return stats
      }
    }),
    {
      name: 'location-storage',
      version: 1,
      storage: createIndexedDBStorage('location-storage'),
      partialize: (state) => ({ locations: state.locations })
    }
  )
)